import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGOS, BRAND } from "../data/content";
import { useBooking } from "./BookingModal";

const links = [
  { label: "About", href: "#intro" },
  { label: "Services", href: "#services" },
  { label: "Academy", href: "#academy" },
  { label: "Work", href: "#work" },
  { label: "Contact", href: "#contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.7, 0, 0.2, 1], delay: 0.6 }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-[color:var(--border)] bg-black/60 backdrop-blur-xl"
            : ""
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <img
              src={LOGOS.main}
              alt={BRAND.name}
              className="h-9 w-auto md:h-11"
            />
          </a>

          <nav className="hidden items-center gap-10 md:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="link-underline font-mono text-[11px] uppercase tracking-[0.25em] text-[color:var(--text)] hover:text-[color:var(--primary)]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-book-btn"
              onClick={openBooking}
              className="hidden items-center gap-2 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--text)] transition hover:bg-[color:var(--primary)] md:inline-flex"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--primary)]" />
              Book Now
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMobile(true)}
              className="flex h-10 w-10 items-center justify-center border border-[color:var(--border)] md:hidden"
              aria-label="Open menu"
            >
              <Menu size={16} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobile && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[120] bg-[color:var(--bg)]"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
          >
            <div className="flex items-center justify-between px-6 py-5">
              <img src={LOGOS.main} alt="99" className="h-9" />
              <button
                onClick={() => setMobile(false)}
                data-testid="mobile-menu-close"
                className="flex h-10 w-10 items-center justify-center border border-[color:var(--border)]"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-col gap-6 px-8 py-16">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => setMobile(false)}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  className="font-serif text-5xl tracking-tight text-[color:var(--text)]"
                >
                  <span className="mr-3 font-mono text-xs text-[color:var(--muted)]">
                    0{i + 1}
                  </span>
                  {l.label}
                </motion.a>
              ))}
              <button
                onClick={() => {
                  setMobile(false);
                  openBooking();
                }}
                data-testid="mobile-book-btn"
                className="mt-6 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em]"
              >
                Book Now →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
